// Utility functions for loading images with timeout, retry, and fallback support
export const IMAGE_LOAD_TIMEOUT = 10000;
export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_DELAY = 1000;

// Fallback image data URI
export const getFallbackImage = () => {
  return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjZTNlM2UzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJhcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OTk5OSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+";
};

/**
 * Load a single image with timeout and retry logic
 * @param {string} src - Image source URL
 * @param {number} timeout - Timeout in milliseconds
 * @param {number} maxRetries - Maximum retry attempts
 * @returns {Promise<string>} - Resolves with image src on success, fallback on failure
 */
export const loadImageWithRetry = async (
  src,
  timeout = IMAGE_LOAD_TIMEOUT,
  maxRetries = MAX_RETRY_ATTEMPTS
) => {
  const loadImage = (imageSrc, attempt = 1) => {
    return new Promise((resolve) => {
      // Validate source
      if (!imageSrc || typeof imageSrc !== "string") {
        resolve(getFallbackImage());
        return;
      }

      const img = new Image();
      let timeoutId;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        img.onload = null;
        img.onerror = null;
      };

      const handleSuccess = () => {
        cleanup();
        resolve(imageSrc);
      };

      const handleError = () => {
        cleanup();
        if (attempt < maxRetries) {
          setTimeout(() => {
            loadImage(imageSrc, attempt + 1).then(resolve);
          }, RETRY_DELAY);
        } else {
          resolve(getFallbackImage());
        }
      };

      timeoutId = setTimeout(() => {
        handleError(); // Timeout = error
      }, timeout);

      img.onload = handleSuccess;
      img.onerror = handleError;
      img.src = imageSrc;
    });
  };

  return loadImage(src);
};

/**
 * Preload multiple images with progress tracking
 * @param {Array<string>} imageSources - Array of image URLs
 * @param {Object} options - Options object
 * @returns {Promise<Object>} - Object with loaded and failed image info
 */

// Preload multiple images with progress tracking
export const preloadImages = async (imageSources, options = {}) => {
  const {
    timeout = IMAGE_LOAD_TIMEOUT,
    maxRetries = MAX_RETRY_ATTEMPTS,
    onProgress = null
  } = options;

  // Results object
  const results = {
    loaded: [],
    failed: [],
    total: imageSources.length
  };

  // Load each image
  const loadPromises = imageSources.map(async (src, index) => {
    try {
      const loadedSrc = await loadImageWithRetry(src, timeout, maxRetries);
      if (loadedSrc === getFallbackImage()) {
        throw new Error("Failed to load image, fallback used");
      }
      // Add to loaded results
      results.loaded.push({ src, loadedSrc, index });
    } catch (error) {
      results.failed.push({ src, error, index });
    } finally {
      if (onProgress) {
        onProgress({
          loaded: results.loaded.length,
          total: results.total,
          failed: results.failed.length
        });
      }
    }
  });

  // Wait for all images to settle
  await Promise.allSettled(loadPromises);
  return results;
};

/**
 * Check if image URL is accessible
 * @param {string} url - Image URL to check
 * @returns {Promise<boolean>} - True if accessible, false otherwise
 */

// Check if image URL is accessible
export const isImageAccessible = async (url) => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    // Create an image element to test loading
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Image cache for performance
 */
const imageCache = new Map();

/**
 * Load image with caching
 * @param {string} src - Image source URL
 * @param {Object} options - Loading options
 * @returns {Promise<string>} - Cached or loaded image src
 */
export const loadImageWithCache = async (src, options = {}) => {
  if (imageCache.has(src)) {
    return imageCache.get(src);
  }

  try {
    const loadedSrc = await loadImageWithRetry(
      src,
      options.timeout || IMAGE_LOAD_TIMEOUT,
      options.maxRetries || MAX_RETRY_ATTEMPTS
    );
    imageCache.set(src, loadedSrc);
    return loadedSrc;
  } catch (error) {
    const fallbackSrc = getFallbackImage();
    imageCache.set(src, fallbackSrc);
    return fallbackSrc;
  }
};

/**
 * Clear image cache
 */
export const clearImageCache = () => {
  imageCache.clear();
};

/**
 * Get cache size
 * @returns {number} - Number of cached images
 */
export const getCacheSize = () => {
  return imageCache.size;
};

/**
 * Performance monitoring
 */
const performanceMetrics = {
  totalLoadTime: 0,
  loadCount: 0,
  averageLoadTime: 0
};

/**
 * Track performance
 * @param {number} durationMs - Load time in milliseconds
 */
export const recordLoadTime = (durationMs) => {
  performanceMetrics.totalLoadTime += durationMs;
  performanceMetrics.loadCount += 1;
  performanceMetrics.averageLoadTime =
    performanceMetrics.totalLoadTime / performanceMetrics.loadCount;
};

/**
 * Get performance metrics
 * @returns {Object} - Performance metrics
 */
export const getPerformanceMetrics = () => {
  return { ...performanceMetrics };
};

/**
 * Reset performance metrics
 */
export const resetPerformanceMetrics = () => {
  performanceMetrics.totalLoadTime = 0;
  performanceMetrics.loadCount = 0;
  performanceMetrics.averageLoadTime = 0;
};
